import type { CountryField } from '@payloadcms/plugin-form-builder/types'
import type { Control, FieldErrorsImpl } from 'react-hook-form'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import React, { useState } from 'react'
import { Controller } from 'react-hook-form'

import { Error } from '../Error'
import { Width } from '../Width'
import { countryOptions } from './options'

export const Country: React.FC<
  CountryField & {
    control: Control
    errors: Partial<FieldErrorsImpl>
  }
> = ({ name, control, errors, label, required, width }) => {
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [customValue, setCustomValue] = useState('')

  return (
    <Width width={width}>
      <Label className="text-foreground" htmlFor={name}>
        {label}
        {required && (
          <span className="required text-foreground">
            * <span className="sr-only">(required)</span>
          </span>
        )}
      </Label>
      <Controller
        control={control}
        defaultValue=""
        name={name}
        render={({ field: { onChange, value } }) => {
          const controlledValue = countryOptions.find((t) => t.value === value)
          const isOther =
            value === 'other' || (value && !countryOptions.find((t) => t.value === value))

          React.useEffect(() => {
            if (isOther && value !== 'other') {
              setCustomValue(value)
              setShowCustomInput(true)
            }
          }, [])

          return (
            <>
              {showCustomInput ? (
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Please specify your country"
                    value={customValue}
                    onChange={(e) => {
                      setCustomValue(e.target.value)
                      onChange(e.target.value)
                    }}
                    className="w-full pr-10"
                    required={required}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomInput(false)
                      setCustomValue('')
                      onChange('')
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear and go back to dropdown"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ) : (
                <Select
                  onValueChange={(val) => {
                    if (val === 'other') {
                      setShowCustomInput(true)
                      onChange(customValue || '')
                    } else {
                      setShowCustomInput(false)
                      setCustomValue('')
                      onChange(val)
                    }
                  }}
                  value={isOther ? 'other' : controlledValue?.value}
                >
                  <SelectTrigger className="w-full" id={name}>
                    <SelectValue placeholder={label} />
                  </SelectTrigger>
                  <SelectContent>
                    {countryOptions.map(({ label, value }) => {
                      return (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            </>
          )
        }}
        rules={{ required }}
      />
      {errors[name] && <Error />}
    </Width>
  )
}
